import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCreateAssetViewMutation, useUpdateHostFieldsMutation } from '@/@generated/codegen/API';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import { trackEvent } from '../analytic/analytics';

type TrackAssetSwitchProps = {
  newAssetID: string;
  assetName: string;
  isLibraryAsset: boolean;
} | null;

export default function useMeetingTrackingAssetStats(
  currentMeetingID: string | undefined | null,
  currentCaseId: string | undefined | null,
  isUserHost: boolean
) {
  const [mutationUpdateHostFields] = useUpdateHostFieldsMutation();
  const [mutationCreateAssetView] = useCreateAssetViewMutation();
  //this is the time that the user started watching the asset
  const [startWatchTime, setStartWatchTime] = useState<number>(0);
  const [currentAssetID, setCurrentAssetID] = useState<string | null>(null);

  const debouncedUpdateHostAsset = useMemo(
    () =>
      debounce(async (meetingId: string, assetId: string | null) => {
        try {
          await mutationUpdateHostFields({
            variables: {
              data: {
                meetingId,
                currentAssetID: {
                  set: assetId,
                },
              },
            },
          });
        } catch (err) {
          captureSentry({
            title: 'Debounced mutationUpdateHostAsset failed',
            detail: { meetingId, assetId, err },
          });
        }
      }, 500),
    []
  );

  const trackAssetOpen = useCallback(
    (assetID: string, assetName: string, _isLibraryAsset = false) => {
      trackEvent('Asset', assetName);

      console.log('Stats | asset opened:', assetID);
      setStartWatchTime(Date.now());
      setCurrentAssetID(assetID);
    },
    [currentAssetID]
  );

  const trackAssetClose = () => {
    if (!currentMeetingID || !currentCaseId || !currentAssetID) {
      console.warn('Couldnt createAssetView statistic');
      captureSentry({
        title: 'MeetingTrackingAssetStats: Couldnt createAssetView statistic',
        detail: {
          currentMeetingID: currentMeetingID,
          currentCaseId: currentCaseId,
          currentAssetID: currentAssetID,
        },
      });
      return;
    }
    const secondsWatched = Math.round((Date.now() - startWatchTime) / 1000);
    console.log('Stats | asset closed:', currentAssetID, secondsWatched);
    void mutationCreateAssetView({
      variables: {
        data: {
          meetingId: currentMeetingID,
          assetId: currentAssetID,
          patientCaseId: currentCaseId,
          timeWatched: secondsWatched,
        },
      },
    });
    setCurrentAssetID(null);
  };

  const trackAssetSwitch = async (args: TrackAssetSwitchProps) => {
    if (!currentMeetingID) {
      captureSentry({
        title: 'MeetingTrackingAssetStats: trackAssetSwitch currentMeetingID is undefined',
        detail: {
          currentMeetingID,
          args,
        },
      });
      return;
    }
    //you can not look at multiple assets at the same time
    if (currentAssetID) {
      trackAssetClose();
      //only set it to null if there was an asset before
      if (!args) {
        if (isUserHost) await debouncedUpdateHostAsset(currentMeetingID, null);
      }
    }
    if (args) {
      trackAssetOpen(args.newAssetID, args.assetName);
      if (isUserHost) await debouncedUpdateHostAsset(currentMeetingID, args.newAssetID);
    }
  };

  /* NOTE: This is so that if the host leaves/crashes and comes back, it will reset the host asset to null*/
  useEffect(() => {
    if (!currentMeetingID) return;
    if (isUserHost) void debouncedUpdateHostAsset(currentMeetingID, null);
  }, [currentMeetingID]);

  return {
    trackAssetSwitch,
  };
}
